<script lang="ts">
import { ref } from 'vue';

// App.vue and FullLayout.vue each mount a <preloader>. Only the most recently
// mounted one plays, so the logo never builds twice on top of itself.
let instanceSeq = 0;
const liveInstances = ref<number[]>([]);
</script>

<script setup lang="ts">
/**
 * Preloader (Design System v3.3): the BuiltSimple logo building on blue.
 *
 *   A  : below the main navigation (module change, a page's own load)
 *   A+ : full screen (log in, log out, switching organisation); on a long wait a
 *        white veil fills the mark diagonally, then completes and holds.
 *
 * Driven by the same store flag the existing callers already flip
 * (`customizer.preloader`); the variant comes from `customizer.preloaderVariant`.
 * Never shown if the flag is cleared within PRELOADER_MS.skip (120 ms). If it is
 * cleared early in the build, the build speeds up to finish within 140 ms. No
 * loop, no text, no percentages. Transform and opacity only.
 *
 * `<Preloader variant="A+" />` (with a variant prop) plays once inline instead,
 * for docs and previews.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, watch } from 'vue';
import { useCustomizerStore, type PreloaderVariant } from '@/stores/customizer';
import { PRELOADER_MS } from '@/utils/preloaderMs';

const props = defineProps<{ variant?: PreloaderVariant }>();

const customizer = useCustomizerStore();
const myId = ++instanceSeq;
const isInline = computed(() => !!props.variant);
const isLive = computed(() => isInline.value || liveInstances.value[liveInstances.value.length - 1] === myId);

type Phase = 'idle' | 'in' | 'out';
const phase = ref<Phase>(props.variant ? 'in' : 'idle');
const shownVariant = ref<PreloaderVariant>(props.variant || 'A');
const topPx = ref(0);
const runId = ref(0);
const rootEl = ref<HTMLElement | null>(null);

let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let endTimers: ReturnType<typeof setTimeout>[] = [];
let finishing = false;

const uid = `bs-pl-${myId}`;

function prefersReducedMotion(): boolean {
    try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
        return false;
    }
}

/** Bottom of the app chrome: Vuetify pads v-main by the app bar's height. */
function measureNavBottom(): number {
    try {
        const main = document.querySelector('.v-main') as HTMLElement | null;
        if (!main) return 0;
        const pad = parseFloat(getComputedStyle(main).paddingTop || '0');
        return Number.isFinite(pad) ? pad : 0;
    } catch {
        return 0;
    }
}

function clearEndTimers() {
    endTimers.forEach((t) => clearTimeout(t));
    endTimers = [];
}

function show() {
    pendingTimer = null;
    if (!customizer.preloader || !isLive.value) return;
    shownVariant.value = customizer.preloaderVariant === 'A+' ? 'A+' : 'A';
    topPx.value = shownVariant.value === 'A' ? measureNavBottom() : 0;
    finishing = false;
    runId.value++;
    phase.value = 'in';
}

function animationsOf(el: HTMLElement | null): Animation[] {
    if (!el || typeof (el as any).getAnimations !== 'function') return [];
    try {
        return el.getAnimations({ subtree: true });
    } catch {
        return [];
    }
}

function animName(a: Animation): string {
    return String((a as any).animationName || '');
}

function remainingMs(a: Animation): number {
    try {
        const timing = a.effect?.getComputedTiming();
        const end = Number(timing?.endTime ?? 0);
        const now = Number(a.currentTime ?? 0);
        return Math.max(0, end - now);
    } catch {
        return 0;
    }
}

/** The page is ready: finish the build quickly, (A+) complete the veil, fade out. */
function finish() {
    if (finishing || phase.value !== 'in') return;
    finishing = true;

    let wait = 0;
    if (!prefersReducedMotion()) {
        const anims = animationsOf(rootEl.value).filter((a) => !animName(a).includes('ds-pl-fade'));
        const isVeil = (a: Animation) => animName(a).includes('ds-pl-veil');
        let buildLeft = 0;
        anims.forEach((a) => {
            if (!isVeil(a)) buildLeft = Math.max(buildLeft, remainingMs(a));
        });
        const buildTarget = Math.min(buildLeft, PRELOADER_MS.rush);
        anims.forEach((a) => {
            const left = remainingMs(a);
            const target = isVeil(a) ? buildTarget + PRELOADER_MS.veilComplete : buildTarget;
            if (left <= 0) return;
            if (target <= 0) a.finish();
            else if (left > target) a.playbackRate = left / target;
        });
        wait = buildTarget;
        if (shownVariant.value === 'A+') wait += PRELOADER_MS.veilComplete + PRELOADER_MS.hold;
    }

    endTimers.push(
        setTimeout(() => {
            phase.value = 'out';
            endTimers.push(
                setTimeout(
                    () => {
                        phase.value = 'idle';
                        finishing = false;
                        if (customizer.preloaderVariant === 'A+' && !customizer.preloader) customizer.preloaderVariant = 'A';
                    },
                    prefersReducedMotion() ? PRELOADER_MS.reducedFade : PRELOADER_MS.fadeOut,
                ),
            );
        }, wait),
    );
}

function onFlag(on: boolean) {
    if (isInline.value) return;
    if (on) {
        if (phase.value === 'out' || (phase.value === 'in' && finishing)) {
            // Asked again while finishing: hold it on the logo instead of restarting.
            clearEndTimers();
            finishing = false;
            phase.value = 'in';
            return;
        }
        if (phase.value === 'in' || pendingTimer) return;
        pendingTimer = setTimeout(show, PRELOADER_MS.skip);
        return;
    }
    if (pendingTimer) {
        // Ready within 120 ms: skipped entirely.
        clearTimeout(pendingTimer);
        pendingTimer = null;
        if (customizer.preloaderVariant === 'A+') customizer.preloaderVariant = 'A';
        return;
    }
    if (phase.value === 'in') nextTick(finish);
}

watch(
    () => customizer.preloader,
    (on) => onFlag(!!on),
);

onMounted(() => {
    if (isInline.value) return;
    liveInstances.value = [...liveInstances.value, myId];
    if (customizer.preloader) onFlag(true);
});

onBeforeUnmount(() => {
    liveInstances.value = liveInstances.value.filter((id) => id !== myId);
    if (pendingTimer) clearTimeout(pendingTimer);
    clearEndTimers();
});
</script>

<template>
    <div
        v-if="isLive && phase !== 'idle'"
        :key="runId"
        ref="rootEl"
        class="ds-preloader"
        :class="[
            'ds-preloader--' + (shownVariant === 'A+' ? 'full' : 'below-nav'),
            { 'ds-preloader--inline': isInline, 'is-out': phase === 'out' },
        ]"
        :style="isInline ? undefined : { top: topPx + 'px' }"
        role="status"
        aria-live="polite"
        aria-label="Loading"
    >
        <div class="ds-preloader__tile">
            <svg viewBox="0 0 620 620" width="56" height="56" aria-hidden="true" focusable="false">
                <defs>
                    <linearGradient :id="uid + '-g'" gradientUnits="userSpaceOnUse" x1="55" y1="0" x2="650" y2="0">
                        <stop offset="0" style="stop-color: var(--bs-primary)" />
                        <stop offset="1" style="stop-color: var(--bs-primary-pressed)" />
                    </linearGradient>
                    <clipPath :id="uid + '-clip'">
                        <g transform="translate(-42 -76)">
                            <path d="M55,365 L304,119 A100,100 0 0 1 446,261 L200,510 Z" />
                            <path d="M385,372 L471,286 A105,105 0 0 1 619,434 L535,518 Z" />
                            <path d="M222,530 L335,417 Q371,381 407,417 L520,535 L372,682 Z" />
                        </g>
                    </clipPath>
                </defs>
                <g transform="translate(-42 -76)" :fill="'url(#' + uid + '-g)'">
                    <path class="ds-preloader__p1" d="M55,365 L304,119 A100,100 0 0 1 446,261 L200,510 Z" />
                    <path class="ds-preloader__p2" d="M385,372 L471,286 A105,105 0 0 1 619,434 L535,518 Z" />
                    <path class="ds-preloader__p3" d="M222,530 L335,417 Q371,381 407,417 L520,535 L372,682 Z" />
                </g>
                <!-- A+ only: the veil that fills the mark diagonally on a long wait. -->
                <g v-if="shownVariant === 'A+'" :clip-path="'url(#' + uid + '-clip)'">
                    <g transform="rotate(-45 310 310)">
                        <rect class="ds-preloader__veil" x="-400" y="-400" width="1420" height="1420" />
                    </g>
                </g>
            </svg>
        </div>
    </div>
</template>

<style scoped>
.ds-preloader {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    /* The old overlay sat at 9999 to clear Vuetify's app bar and drawers; the
       design-system --bs-z-toast (260) sits below those, so the old layer stays. */
    z-index: 9999;
    display: grid;
    place-items: center;
    background: var(--bs-primary);
    animation: ds-pl-fade-in 80ms linear both;
}

.ds-preloader--inline {
    position: relative;
    inset: auto;
    height: 200px;
    border-radius: var(--bs-radius-lg);
    overflow: hidden;
    z-index: auto;
}

.ds-preloader.is-out {
    animation: ds-pl-fade-out 120ms linear both;
    pointer-events: none;
}

.ds-preloader__tile {
    width: 88px;
    height: 88px;
    border-radius: 22%;
    background: var(--bs-surface);
    display: grid;
    place-items: center;
    animation: ds-pl-tile 180ms var(--bs-ease) both;
}

.ds-preloader__tile svg {
    overflow: visible;
}

.ds-preloader__p1,
.ds-preloader__p2,
.ds-preloader__p3 {
    transform-box: fill-box;
    animation-duration: 290ms;
    animation-timing-function: var(--bs-ease);
    animation-fill-mode: both;
}

/* Tile scales in, then the three pieces fly in and lock: about 460 ms in all. */
.ds-preloader__p1 {
    animation-name: ds-pl-p1;
    animation-delay: 110ms;
}

.ds-preloader__p2 {
    animation-name: ds-pl-p2;
    animation-delay: 140ms;
}

.ds-preloader__p3 {
    animation-name: ds-pl-p3;
    animation-delay: 170ms;
}

/* Starts once the build is done; fills for up to 2.6 s, then waits (no loop). */
.ds-preloader__veil {
    fill: var(--bs-surface);
    opacity: 0.55;
    animation: ds-pl-veil 2600ms cubic-bezier(0.25, 0.1, 0.25, 1) 460ms both;
}

@keyframes ds-pl-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes ds-pl-fade-out {
    from { opacity: 1; }
    to { opacity: 0; }
}

@keyframes ds-pl-tile {
    from { opacity: 0; transform: scale(0.88); }
    to { opacity: 1; transform: none; }
}

@keyframes ds-pl-p1 {
    from { opacity: 0; transform: translate(-56px, -56px); }
    30% { opacity: 1; }
    to { opacity: 1; transform: none; }
}

@keyframes ds-pl-p2 {
    from { opacity: 0; transform: translate(56px, -56px); }
    30% { opacity: 1; }
    to { opacity: 1; transform: none; }
}

@keyframes ds-pl-p3 {
    from { opacity: 0; transform: translate(0, 79px); }
    30% { opacity: 1; }
    to { opacity: 1; transform: none; }
}

@keyframes ds-pl-veil {
    from { transform: translateY(1420px); }
    to { transform: translateY(0); }
}

/* Reduced motion: a plain fade of the tile; nothing moves. */
@media (prefers-reduced-motion: reduce) {
    .ds-preloader__tile {
        animation: ds-pl-fade-in 120ms linear both;
    }

    .ds-preloader__p1,
    .ds-preloader__p2,
    .ds-preloader__p3,
    .ds-preloader__veil {
        animation: none;
    }

    .ds-preloader__veil {
        opacity: 0;
    }
}
</style>
